import { clearAllBodyScrollLocks } from 'body-scroll-lock-upgrade';
import tippy from 'tippy.js';
import { t } from 'javascripts/utils/withTranslation';

export default class LinkDropdown {
  constructor($el) {
    this.$el = $el;
    this.$toggleButton = this.$el.querySelector('.link-dropdown__trigger');
    const menuNode = this.$toggleButton?.getAttribute('aria-controls');

    if (!menuNode) {
      console.error('No aria-controls attribute found on trigger button.');
      return;
    }

    this.$menu = $el.querySelector(`#${menuNode}`);
    this.$items = this.$menu?.querySelectorAll('.dropdown-menu__link');
    this.tooltipTemplate = this.$toggleButton?.getAttribute('data-template');
    this.$tooltipContent = this.$el.querySelector(`#${this.tooltipTemplate}`);
    const currentIndex = [...(this.$items || [])].findIndex((item, index) =>
      this.$items[index]?.classList.contains('current')
    );
    this.currentOptionIndex = currentIndex > -1 ? currentIndex : 0;

    // Binded events
    this.bindedOnClick = this.onClick.bind(this);
    this.bindedOnKeypress = this.onKeypress.bind(this);
    this.onOutsideClickBinded = this.onOutsideClick.bind(this);
    this.onOverlayCloseBinded = this.onOverlayClose.bind(this);

    if (this.$toggleButton) {
      this.init();
    } else {
      console.error('No trigger button found.');
    }
  }

  init() {
    const newEl = document.createElement('button');
    newEl.setAttribute('class', this.$toggleButton.getAttribute('class'));
    newEl.setAttribute(
      'aria-controls',
      this.$toggleButton.getAttribute('aria-controls')
    );
    newEl.setAttribute('role', 'combobox');
    newEl.setAttribute('aria-expanded', false);
    if (this.$toggleButton.getAttribute('aria-labelledby')) {
      newEl.setAttribute(
        'aria-labelledby',
        this.$toggleButton.getAttribute('aria-labelledby')
      );
    } else {
      newEl.setAttribute(
        'aria-label',
        this.$toggleButton.getAttribute('aria-label') || t('Dropdown öffnen')
      );
    }
    newEl.innerHTML = this.$toggleButton.innerHTML;
    this.$el.replaceChild(newEl, this.$toggleButton);
    this.$toggleButton = newEl;

    // Add events
    this.$toggleButton.addEventListener('click', this.bindedOnClick);
    document.addEventListener('overlayClose', this.onOverlayCloseBinded);

    // Tooltip
    if (this.$tooltipContent) {
      this.tippyInstance = tippy(this.$toggleButton, {
        content: this.$tooltipContent.innerHTML,
        allowHTML: true,
        placement: 'bottom',
        animation: 'shift-away',
        arrow: false,
        theme: 'bw',
      });
    }
  }

  deconstructor() {
    // Remove events
    this.$toggleButton.removeEventListener('click', this.bindedOnClick);

    // Reset panel
    this.closeMenu();
  }

  closeMenu(externalClose = false) {
    // Change toggle button
    this.$toggleButton.setAttribute('aria-expanded', 'false');
    this.$toggleButton.classList.remove('link-dropdown__trigger--active');

    // Close menu
    this.$el.classList.remove('open');

    // Add focus back on toggle button if the menu was not closed by the overlayClose event
    if (!externalClose) {
      this.$toggleButton.focus();
    }

    this.tippyInstance?.enable();
    document.removeEventListener('keydown', this.bindedOnKeypress);
    document.removeEventListener('click', this.onOutsideClickBinded);
  }

  openMenu() {
    // Change toggle button
    this.$toggleButton.setAttribute('aria-expanded', 'true');
    this.$toggleButton.classList.add('link-dropdown__trigger--active');

    // Show panel
    this.$el.classList.add('open');
    this.focusCurrentOption();

    // Add keydown and click outside
    document.addEventListener('keydown', this.bindedOnKeypress);
    document.addEventListener('click', this.onOutsideClickBinded);

    // Close every other overlay that listens to the overlayClose event on document
    const overlayCloseEvent = new CustomEvent('overlayClose', {
      bubbles: true,
      detail: { target: this.$el },
    });

    document.dispatchEvent(overlayCloseEvent);
    clearAllBodyScrollLocks();
    this.tippyInstance?.disable();
  }

  toggleDropdown() {
    const isOpen = this.$toggleButton.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closeMenu();
    }
    if (!isOpen) {
      this.openMenu();
    }
  }

  onClick(event) {
    // Get current state of panel
    const isClosed =
      this.$toggleButton.getAttribute('aria-expanded') !== 'true';

    // Switch state
    window.requestAnimationFrame(() => {
      if (isClosed) {
        this.openMenu();
      } else {
        this.closeMenu();
      }
    });

    event.preventDefault();
  }

  focusCurrentOption() {
    const currentOption = this.$items?.[this.currentOptionIndex];
    currentOption.classList.add('current');
    currentOption.focus();
    currentOption.scrollIntoView({
      block: 'nearest',
    });
    this.$items?.forEach((option, index) => {
      if (option !== currentOption) {
        option.classList.remove('current');
      }
    });
  }

  onKeypress(event) {
    // Get current state of panel
    const isOpen = this.$toggleButton.getAttribute('aria-expanded') === 'true';
    event.preventDefault();
    const { key } = event;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' '];

    if (!isOpen && openKeys.includes(key)) {
      this.toggleDropdown();
    } else if (isOpen) {
      switch (key) {
        case 'Escape':
          this.toggleDropdown();
          break;
        case 'ArrowDown':
          this.moveFocusDown();
          break;
        case 'ArrowUp':
          this.moveFocusUp();
          break;
        case 'Enter':
        case ' ':
          this.selectCurrentOption();
          break;
        default:
          break;
      }
    }
  }

  moveFocusDown() {
    if (this.currentOptionIndex < (this.$items?.length || 0) - 1) {
      this.currentOptionIndex += 1;
    } else {
      this.currentOptionIndex = 0;
    }
    this.focusCurrentOption();
  }

  moveFocusUp() {
    if (this.currentOptionIndex > 0) {
      this.currentOptionIndex -= 1;
    } else {
      this.currentOptionIndex = (this.$items?.length || 0) - 1;
    }
    this.focusCurrentOption();
  }

  onOutsideClick(event) {
    const $target = event.target;

    if ($target) {
      const isInside = $target === this.$menu || this.$menu.contains($target);
      const isButton =
        $target === this.$toggleButton || this.$toggleButton.contains($target);

      if (!isInside && !isButton) {
        this.closeMenu();
      }
    }
  }

  selectCurrentOption() {
    const selectedOption = this.$items[this.currentOptionIndex];
    const href = selectedOption.getAttribute('href');
    window.location.href = href;
    this.closeMenu();
  }

  onOverlayClose(event) {
    if (event.detail?.target !== this.$el) {
      this.closeMenu(true);
    }
  }
}
